/**
 * Taxonomie cognitive M3 — DEC-019.
 *
 * 8 domaines DSM-5/Lezak canoniques avec slugs EN strict (apps multilingues
 * standard, URL langue-agnostique, UI label localisé via i18n).
 *
 * 6 domaines actifs + 2 pending (Perception + Motor Skills hors scope M3 —
 * Perception ouverture M5+, Motor confirmé hors scope solo digital DEC-017).
 *
 * 36 jeux mappés primary + 5 cross-domain assumés (face-facts SC primary
 * cross M&L, freeze-frame CA primary cross EF, double-decision CA primary
 * cross PS, logic-trail EF primary cross PS, number-crunch EF primary cross
 * M&L).
 *
 * Mapping détaillé : chiron-taxonomy-refonte.md §2 (canonique).
 */
/**
 * Cross-domain map : jeu primary à un domaine mais mobilise un secondary.
 * Affiché en drawer wiki "Travaille aussi : <secondary>".
 */
export const CROSS_DOMAINS = {
    'freeze-frame': 'executive', // CA primary + EF secondary (Go/No-Go inhibition)
    'double-decision': 'speed', // CA primary + PS secondary (UFOV speed)
    'logic-trail': 'speed', // EF primary + PS secondary (TMT-B)
    'number-crunch': 'memory', // EF primary + M&L secondary (mental arithmetic WM)
    'face-facts': 'memory', // SC primary + M&L secondary (face memory)
};
export const CATEGORIES = [
    {
        slug: 'memory',
        labelKey: 'library:categories.memory.label',
        descKey: 'library:categories.memory.desc',
        tint: 'core',
        status: 'active',
        gameSlugs: [
            'memory-grid',
            'memory-matrix',
            'sequence',
            'digit-span-backward',
            'juggle-factor',
            'prospective-memory',
            'mental-map',
            'path-finder',
            'true-north',
            'minds-eye',
            '3d-rotation',
        ],
        subDomains: [
            { slug: 'spatial-working-memory', labelKey: 'library:categories.memory.subdomains.spatial-working-memory' },
            { slug: 'verbal-working-memory', labelKey: 'library:categories.memory.subdomains.verbal-working-memory' },
            { slug: 'working-memory-updating', labelKey: 'library:categories.memory.subdomains.working-memory-updating' },
            { slug: 'visual-recognition-memory', labelKey: 'library:categories.memory.subdomains.visual-recognition-memory' },
            { slug: 'prospective-memory', labelKey: 'library:categories.memory.subdomains.prospective-memory' },
            { slug: 'spatial-navigation', labelKey: 'library:categories.memory.subdomains.spatial-navigation' },
            { slug: 'mental-rotation', labelKey: 'library:categories.memory.subdomains.mental-rotation' },
        ],
    },
    {
        slug: 'attention',
        labelKey: 'library:categories.attention.label',
        descKey: 'library:categories.attention.desc',
        tint: 'network',
        status: 'active',
        gameSlugs: [
            'lost-in-migration',
            'divided-attention',
            'target-tracker',
            'freeze-frame',
            'train-of-thought',
            '3d-mot',
            'hawk-eye',
            'double-decision',
        ],
        subDomains: [
            { slug: 'selective-attention', labelKey: 'library:categories.attention.subdomains.selective-attention' },
            { slug: 'sustained-attention', labelKey: 'library:categories.attention.subdomains.sustained-attention' },
            { slug: 'divided-attention', labelKey: 'library:categories.attention.subdomains.divided-attention' },
        ],
    },
    {
        slug: 'executive',
        labelKey: 'library:categories.executive.label',
        descKey: 'library:categories.executive.desc',
        tint: 'media',
        status: 'active',
        gameSlugs: [
            'inhibition',
            'color-match',
            'mind-bender',
            'logic-trail',
            'number-crunch',
            'alternative-uses',
            'bart-risk',
            'jol-confidence',
            'wcst',
            'antisaccade',
            'stop-signal',
        ],
        subDomains: [
            { slug: 'inhibitory-control', labelKey: 'library:categories.executive.subdomains.inhibitory-control' },
            { slug: 'cognitive-flexibility', labelKey: 'library:categories.executive.subdomains.cognitive-flexibility' },
            { slug: 'reasoning', labelKey: 'library:categories.executive.subdomains.reasoning' },
            { slug: 'decision-under-risk', labelKey: 'library:categories.executive.subdomains.decision-under-risk' },
            { slug: 'metacognitive-monitoring', labelKey: 'library:categories.executive.subdomains.metacognitive-monitoring' },
        ],
    },
    {
        slug: 'speed',
        labelKey: 'library:categories.speed.label',
        descKey: 'library:categories.speed.desc',
        tint: 'studio',
        status: 'active',
        gameSlugs: ['reaction-sport', 'speed-match', 'visual-sweeps', 'sound-sweeps', 'symbol-coding', 'trail-making-a'],
        subDomains: [],
    },
    {
        slug: 'language',
        labelKey: 'library:categories.language.label',
        descKey: 'library:categories.language.desc',
        tint: 'media',
        status: 'active',
        gameSlugs: ['word-pyramids', 'verbal-fluency'],
        subDomains: [],
    },
    {
        slug: 'social',
        labelKey: 'library:categories.social.label',
        descKey: 'library:categories.social.desc',
        tint: 'studio',
        status: 'active',
        gameSlugs: [
            'eyes-test-tom',
            'faux-pas-detection',
            'face-facts',
            'face-to-face',
            'cognitive-reappraisal',
        ],
        subDomains: [
            { slug: 'theory-of-mind', labelKey: 'library:categories.social.subdomains.theory-of-mind' },
            { slug: 'emotion-regulation', labelKey: 'library:categories.social.subdomains.emotion-regulation' },
        ],
    },
    {
        slug: 'perception',
        labelKey: 'library:categories.perception.label',
        descKey: 'library:categories.perception.desc',
        tint: 'network',
        status: 'pending',
        gameSlugs: [],
        subDomains: [],
    },
    {
        slug: 'motor',
        labelKey: 'library:categories.motor.label',
        descKey: 'library:categories.motor.desc',
        tint: 'network',
        status: 'pending',
        gameSlugs: [],
        subDomains: [],
    },
];
/** Domaines actifs M3 (6) — utilisé par session-du-jour algo + radar score. */
export const ACTIVE_CATEGORIES = CATEGORIES.filter((c) => c.status === 'active');
export const CATEGORY_BY_SLUG = CATEGORIES.reduce((acc, cat) => {
    acc[cat.slug] = cat;
    return acc;
}, {});
export const GAME_TO_CATEGORY = CATEGORIES.reduce((acc, cat) => {
    for (const game of cat.gameSlugs)
        acc[game] = cat.slug;
    return acc;
}, {});
export function isCategorySlug(value) {
    return value in CATEGORY_BY_SLUG;
}
/** Retourne CategoryDef (incluant pending). null si slug inconnu. */
export function getCategory(slug) {
    return isCategorySlug(slug) ? CATEGORY_BY_SLUG[slug] : null;
}
export function getCategoryForGame(gameSlug) {
    const cat = GAME_TO_CATEGORY[gameSlug];
    return cat ? CATEGORY_BY_SLUG[cat] : null;
}
/** Secondary domain pour cross-domain games. null si pas cross. */
export function getCrossDomainForGame(gameSlug) {
    return CROSS_DOMAINS[gameSlug] ?? null;
}
/** 36 jeux mappés (active only, pending cats vides). */
export const ALL_GAME_SLUGS = ACTIVE_CATEGORIES.flatMap((c) => c.gameSlugs);
/**
 * Filtre les jeux d'un domaine par sub-domain (utilisé par CategoryDetail
 * pour query param ?subdomain=<sub>). Mapping game → sub-domain dérivé
 * du brain repo wiki frontmatter (`cognitive_subdomain` field) ; à terme
 * exposable via game-wiki.json. Pour M3 ship : sub-domain de chaque jeu
 * inferred ici depuis Chiron §2.
 */
export const GAME_TO_SUBDOMAIN = {
    // memory sub-domains
    'memory-grid': 'spatial-working-memory',
    'memory-matrix': 'visual-recognition-memory',
    'sequence': 'verbal-working-memory',
    'digit-span-backward': 'verbal-working-memory',
    'juggle-factor': 'working-memory-updating',
    'prospective-memory': 'prospective-memory',
    'mental-map': 'spatial-navigation',
    'path-finder': 'spatial-navigation',
    'true-north': 'spatial-navigation',
    'minds-eye': 'mental-rotation',
    '3d-rotation': 'mental-rotation',
    // attention sub-domains
    'lost-in-migration': 'selective-attention',
    'train-of-thought': 'selective-attention',
    'hawk-eye': 'selective-attention',
    'target-tracker': 'sustained-attention',
    'freeze-frame': 'sustained-attention',
    'divided-attention': 'divided-attention',
    '3d-mot': 'divided-attention',
    'double-decision': 'selective-attention',
    // executive sub-domains
    'inhibition': 'inhibitory-control',
    'color-match': 'inhibitory-control',
    'antisaccade': 'inhibitory-control',
    'stop-signal': 'inhibitory-control',
    'mind-bender': 'cognitive-flexibility',
    'alternative-uses': 'cognitive-flexibility',
    'wcst': 'cognitive-flexibility',
    'logic-trail': 'reasoning',
    'number-crunch': 'reasoning',
    'bart-risk': 'decision-under-risk',
    'jol-confidence': 'metacognitive-monitoring',
    // social sub-domains
    'eyes-test-tom': 'theory-of-mind',
    'faux-pas-detection': 'theory-of-mind',
    'face-facts': 'theory-of-mind',
    'face-to-face': 'theory-of-mind',
    'cognitive-reappraisal': 'emotion-regulation',
    // speed + language : pas de sub-domain layer (jeux surface direct)
};
export function getSubDomainForGame(gameSlug) {
    return GAME_TO_SUBDOMAIN[gameSlug] ?? null;
}
//# sourceMappingURL=categories.js.map